import React from "react";
import { Link } from "react-router-dom";
import LandingFooter from "../_components/Footer/LandingFooter";

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Navigation Header */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800">
                InviteWith<span className="text-rose-500">Love</span>
              </h1>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex-1">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-lg text-gray-600 bg-gray-100 px-4 py-2 rounded-lg inline-block">
              Last updated: 25 August 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-gray prose-lg max-w-none">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8 rounded-r-lg">
              <p className="text-blue-800 font-medium mb-2">Quick Summary</p>
              <p className="text-blue-700 text-sm leading-relaxed">
                We respect your privacy and comply with New Zealand privacy laws. This policy explains how we collect, 
                use, and protect your information when using our digital invitation service.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">1. Introduction</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                New Axis Labs Limited ("we," "our," or "us") is committed to protecting your
                privacy. This Privacy Policy explains how we collect, use, disclose,
                and safeguard your information when you use our digital invitation
                service at <span className="font-medium text-rose-600">invitewithlove.com</span>.
              </p>
              <p className="leading-relaxed">
                We comply with the <span className="font-medium">Privacy Act 2020 (NZ)</span> and other applicable laws
                when dealing with personal information.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">Personal Information</h3>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                We may collect personal information that you provide directly to us,
                including:
              </p>
              <ul className="space-y-2 list-disc list-inside text-gray-700">
                <li>Name and contact information (email address, phone number)</li>
                <li>Event details (date, location, type of event)</li>
                <li>Guest information (names, email addresses, phone numbers)</li>
                <li>Account credentials</li>
                <li>Payment information (processed securely through third-party providers)</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">Automatically Collected Information</h3>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">When you use our service, we may automatically collect:</p>
              <ul className="space-y-2 list-disc list-inside text-gray-700">
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Usage data (pages visited, features used, time spent)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">3. How We Use Your Information</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                We process your personal information for the following purposes:
              </p>
              <ul className="space-y-2 list-disc list-inside text-gray-700 mb-6">
                <li>To provide, operate, and maintain our digital invitation service</li>
                <li>To process and deliver invitations to your guests</li>
                <li>To manage your account and provide customer support</li>
                <li>To process payments and prevent fraud</li>
                <li>To send you service-related communications</li>
                <li>To send you marketing communications (where you have opted in)</li>
                <li>To improve our services and develop new features</li>
                <li>To comply with legal obligations</li>
              </ul>
              <p className="mb-4 leading-relaxed font-medium text-gray-800">The legal bases for processing are:</p>
              <ul className="space-y-2 list-disc list-inside text-gray-700">
                <li>Performance of our contract with you</li>
                <li>Compliance with legal obligations</li>
                <li>Your consent (e.g., for marketing communications)</li>
                <li>Our legitimate business interests (e.g., improving services, ensuring security)</li>
              </ul>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">4. Information Sharing</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                We do not sell or rent your personal information. We may share your
                information with:
              </p>
              <ul className="space-y-2 list-disc list-inside text-gray-700 mb-4">
                <li>Service providers who assist in operating our platform</li>
                <li>Payment processors for transaction handling</li>
                <li>Your designated guests (invitation recipients)</li>
                <li>Legal authorities when required by law</li>
              </ul>
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4">
                <p className="text-amber-800 text-sm">
                  <span className="font-medium">Important:</span> When you upload guest details, you confirm that you have their
                  permission to share this information with us.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">5. Cookies and Tracking</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">We use cookies and similar technologies to:</p>
              <ul className="space-y-2 list-disc list-inside text-gray-700 mb-4">
                <li>Enable essential site functionality</li>
                <li>Analyse usage and improve our services</li>
                <li>Personalise user experience</li>
              </ul>
              <p className="leading-relaxed text-gray-600">
                You can manage or disable cookies through your browser settings, but
                some features of our service may not function properly.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">6. Data Security</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                We implement appropriate technical and organisational measures to
                protect your personal information against unauthorised access,
                alteration, disclosure, or destruction.
              </p>
              <div className="bg-green-50 border-l-4 border-green-400 p-4">
                <p className="text-green-800 text-sm">
                  <span className="font-medium">Breach Notification:</span> If we become aware of a privacy breach that is likely to cause
                  serious harm, we will notify you and the Office of the Privacy
                  Commissioner as required by law.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">7. Your Rights Under New Zealand Privacy Laws</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed font-medium">Under the Privacy Act 2020, you have the right to:</p>
              <ul className="space-y-2 list-disc list-inside text-gray-700 mb-4">
                <li>Access your personal information</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to certain uses of your information</li>
                <li>Lodge a complaint with the Office of the Privacy Commissioner</li>
              </ul>
              <p className="text-gray-600 text-sm">Requests can be made by contacting us (see Section 12).</p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">8. Data Retention</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                We retain your personal information for as long as necessary to
                provide our services and comply with legal obligations.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-rose-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Event data is typically retained for <span className="font-medium">12 months</span> after the event date, unless you request earlier deletion.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-rose-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Payment data is retained in accordance with accounting and tax obligations.</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">9. International Data Transfers</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                Your information may be transferred to and processed in countries
                other than New Zealand (for example, where our cloud service
                providers host data in Australia or the United States).
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <p className="text-blue-800 text-sm">
                  <span className="font-medium">Safeguards:</span> Where we transfer data outside New Zealand, we ensure that the
                  recipient is subject to comparable safeguards as required under the
                  Privacy Act 2020.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">10. Children's Privacy</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="leading-relaxed">
                Our service is not intended for children under <span className="font-medium">16 years of age</span>. We
                do not knowingly collect personal information from children under 16.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">11. Changes to This Policy</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. Updates will be
                posted on this page with the "Last updated" date revised
                accordingly. If changes are significant, we will provide additional
                notice (such as by email).
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">12. Contact Us</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
              <p className="mb-4 leading-relaxed">
                If you have questions about this Privacy Policy or how we handle
                your personal information, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <address className="not-italic text-gray-700 space-y-1">
                  <div className="font-medium text-gray-900">New Axis Labs Limited</div>
                  <div>Auckland, New Zealand</div>
                  <div>
                    Email: <a href="mailto:privacy@newaxislabs.com" className="text-rose-600 hover:text-rose-700 font-medium">privacy@newaxislabs.com</a>
                  </div>
                  <div>
                    Website: <a
                      href="https://newaxislabs.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-rose-600 hover:text-rose-700 font-medium"
                    >
                      newaxislabs.com
                    </a>
                  </div>
                </address>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default PrivacyPolicy;
